import {
  GET_POSTS_PAGINATION,
  GET_POSTS_BY_PAGE,
  GET_WALL_PROFILES,
  GET_WALL_PERMISSIONS,
  GET_WALL_COMPANY_PERMISSIONS,
  GET_WALL_BANNED_WORDS,
  GET_WALL_STATES,
} from "@/apollo/queries";
import {
  WALL_STATE,
  WALL_ADD,
  WALL_ADD_PROFILE,
  WALL_EDIT_PROFILE,
  WALL_DELETE_PROFILE,
  WALL_EDIT,
  WALL_SAVE_PERMISSIONS,
  WALL_ADD_BANNED_WORDS,
  WALL_DELETE_BANNED_WORD,
} from "@/apollo/mutations";
import { apolloClient } from "../../../vue-apollo";

// Los permisos del muro se manejan solo con tres operaciones: GetPermisos
// (catálogo), GetPermisoEmpresa (config de la empresa) y createOrUpdatePermisos
// (guardado). CreatePermisoEmpresa y UpdatePermisoEmpresa quedan deprecadas y
// no deben usarse como respaldo.
//
// `createOrUpdatePermisos` borra todos los permisos de la empresa antes de
// recrearlos, así que la lista enviada debe estar completa: ver
// `buildPermissionIds`.

// Cada opción del catálogo expone un único flag no nulo, que corresponde al eje
// que representa. Mapea el flag del backend con la clave de `wall_settings`.
const SETTING_BY_FLAG = {
  moderacion: "moderation",
  publicacion: "user_posts",
  comentarios: "comments",
};

// Elige, por cada grupo del catálogo, la única opción cuyo flag coincide con el
// setting pedido. Devuelve también los grupos sin coincidencia: como el backend
// borra y recrea, enviar una lista incompleta equivale a perder ese eje.
function buildPermissionIds(catalog, settings) {
  const ids = [];
  const missing = [];
  const groups = Array.isArray(catalog) ? catalog : [];

  groups.forEach((group) => {
    const opciones = Array.isArray(group.opciones) ? group.opciones : [];
    const match = opciones.find((opt) =>
      Object.keys(SETTING_BY_FLAG).some(
        (flag) =>
          opt[flag] !== null &&
          opt[flag] !== undefined &&
          Boolean(opt[flag]) === Boolean(settings[SETTING_BY_FLAG[flag]]),
      ),
    );
    if (match) {
      ids.push(parseInt(match.id_permiso, 10));
    } else {
      missing.push(group.titulo || "sin título");
    }
  });

  return { ids: [...new Set(ids)], missing };
}

export default {
  namespaced: true,
  state: {
    pagination_data: [],
    paginated_posts: [],
    profile_list: [],
    post_edit: {},
    wall_settings: {
      moderation: false,
      user_posts: true,
      comments: true,
    },
    wall_permissions_catalog: [],
    wall_company_permissions: [],
    wall_settings_loading: false,
    banned_words: [],
    // Catálogo de FindEstadosMuro. Queda vacío si la consulta falla, y en ese
    // caso el filtro de estado no se muestra.
    wall_states: [],
  },

  mutations: {
    DELETE_POST(state, post_) {
      let index_post = state.paginated_posts.findIndex(
        (post) => post.id_muro == post_.id_muro,
      );
      if (index_post != -1) {
        state.paginated_posts.splice(index_post, 1);
      }
    },
    DISABLE_POST(state, post_) {
      let index_post = state.paginated_posts.findIndex(
        (post) => post.id_muro == post_.id_muro,
      );
      if (index_post != -1) {
        let changed_post = state.paginated_posts[index_post];
        changed_post.id_estado_fk = post_.id_estado_fk;
        Object.assign(state.paginated_posts[index_post], changed_post);
      }
    },
    EDIT_POST(state, post) {
      let index = state.paginated_posts.findIndex(
        (e) => e.id_muro == post.id_muro,
      );
      // console.log("Index en edit: ", index);
      // console.log("revisado resultado de post: ", post);
      if (index != -1) {
        //state.paginated_posts[index] = post;
        Object.assign(state.paginated_posts[index], post);
        // console.log("data pusheada: ", state.paginated_posts[index]);
        // console.log("data total state: ", state.paginated_posts);
      }
    },
    SET_POST_FOR_EDIT(state, post) {
      if (post.length > 0) {
        state.post_edit = post[0];
      }
    },
    ADD_POST(state, post) {
      //console.log("NUEVO POST A INGRESAR AL OBJETO: ", post);
      state.paginated_posts.unshift(post);
    },
    ADD_PROFILE(state, profile) {
      state.profile_list.unshift(profile);
    },
    UPDATE_PROFILE(state, profile_) {
      let index_profile = state.profile_list.findIndex(
        (profile) => profile.id_perfil == profile_.id_perfil,
      );
      if (index_profile != -1) {
        Object.assign(state.profile_list[index_profile], profile_);
      }
    },
    REMOVE_PROFILE(state, id_profile_) {
      let index_profile = state.profile_list.findIndex(
        (profile) => profile.id_perfil == id_profile_,
      );
      if (index_profile != -1) {
        state.profile_list.splice(index_profile, 1);
      }
    },
    SET_PROFILE_LIST(state, profiles) {
      state.profile_list = profiles;
    },
    SET_PAGINATION(state, posts) {
      state.pagination_data = posts;
    },
    SET_PAGINATED_POST(state, posts) {
      state.paginated_posts = posts;
    },
    SET_WALL_SETTINGS(state, settings) {
      state.wall_settings = { ...state.wall_settings, ...settings };
    },
    SET_WALL_PERMISSIONS_CATALOG(state, catalog) {
      state.wall_permissions_catalog = catalog;
    },
    SET_WALL_COMPANY_PERMISSIONS(state, permissions) {
      state.wall_company_permissions = permissions;
    },
    SET_WALL_SETTINGS_LOADING(state, loading) {
      state.wall_settings_loading = loading;
    },
    SET_BANNED_WORDS(state, words) {
      state.banned_words = words;
    },
    SET_WALL_STATES(state, states) {
      state.wall_states = states;
    },
    ADD_BANNED_WORDS(state, words) {
      words.forEach((word) => {
        const exists = state.banned_words.some(
          (w) => w.id_palabra == word.id_palabra,
        );
        if (!exists) {
          state.banned_words.unshift(word);
        }
      });
    },
    REMOVE_BANNED_WORD(state, id_palabra) {
      let index = state.banned_words.findIndex(
        (w) => w.id_palabra == id_palabra,
      );
      if (index != -1) {
        state.banned_words.splice(index, 1);
      }
    },
  },
  actions: {
    async getPostsPagination({ commit }, data_) {
      let response = null;
      let params = {
        query: GET_POSTS_PAGINATION,
        variables: data_,
        fetchPolicy: "network-only",
      };
      await apolloClient
        .query(params)
        .then((res) => {
          if (!res.loading) response = res.data.TotalPaginasPublicacionesMuro;
          if (response != null) {
            commit("SET_PAGINATION", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async getAllPostsByPage({ commit }, data_) {
      let response = null;
      let params = {
        query: GET_POSTS_BY_PAGE,
        variables: data_,
        fetchPolicy: "network-only",
      };
      await apolloClient
        .query(params)
        .then((res) => {
          if (!res.loading) response = res.data.PublicacionesMuroPaginas;
          if (response != null) {
            commit("SET_PAGINATED_POST", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    // metodo de paginacion y envia los datos correspondiente a muro según pagina, esto depreca metodo de getAllPostsByPage
    async getPostPaginasPostCompleto({ dispatch, commit }, payload) {
      let response = null;
      const paginatedMuro = await dispatch(
        "getPostsPagination",
        payload.data2_,
      );
      let pagina = payload.pagina;
      let ids_postmuro = [];
      if (paginatedMuro) {
        paginatedMuro.forEach((data) => {
          if (data.pagina == pagina) {
            ids_postmuro = data.muros;
          }
        });
      }
      let data_ = {
        id_empresa: payload.data2_.id_empresa,
        plataforma: payload.data2_.plataforma,
        id_muro: ids_postmuro,
      };
      let params = {
        query: GET_POSTS_BY_PAGE,
        variables: data_,
        fetchPolicy: "network-only",
      };
      try {
        await apolloClient.query(params).then((res) => {
          //console.log("respuest de server getPostPaginasPostCompleto: ", res.data.PublicacionesMuroPaginas);
          if (!res.loading) response = res.data.PublicacionesMuroPaginas;
        });
      } catch (e) {
        console.error(e);
      }
      return new Promise((resolve) => {
        if (response != null) {
          if (response.length == 0) {
            resolve(false);
            commit("SET_PAGINATED_POST", response);
          } else {
            resolve(response);
            commit("SET_PAGINATED_POST", response);
          }
        } else {
          resolve(false);
        }
      });
    },
    async getProfiles({ commit }, data_) {
      let response = null;
      let params = {
        query: GET_WALL_PROFILES,
        variables: data_,
        fetchPolicy: "network-only",
      };
      await apolloClient
        .query(params)
        .then((res) => {
          if (!res.loading) response = res.data.PerfilesListAll;
          if (response != null) {
            commit("SET_PROFILE_LIST", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async deletePost({ commit }, data_) {
      let response = null;
      let params = {
        mutation: WALL_STATE,
        variables: data_,
      };
      //console.log("params cuando es con perfiles de usuario y no con usuario posteador: ", data_);
      await apolloClient
        .mutate(params)
        .then((res) => {
          if (!res.loading) response = res.data.ChangeEstadoMuro;
          if (response != null) {
            commit("DELETE_POST", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async disablePost({ commit }, data_) {
      let response = null;
      let params = {
        mutation: WALL_STATE,
        variables: data_,
      };
      await apolloClient
        .mutate(params)
        .then((res) => {
          //console.log('disabled', res.data.ChangeEstadoMuro);
          if (!res.loading) response = res.data.ChangeEstadoMuro;
          if (response != null) {
            commit("DISABLE_POST", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async addPost({ commit }, data_) {
      let response = null;
      let params = {
        mutation: WALL_ADD,
        variables: data_,
      };
      await apolloClient
        .mutate(params)
        .then((res) => {
          if (!res.loading) response = res.data.crearMuroAdmin;
          if (response != null) {
            commit("ADD_POST", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async editPost({ commit }, data_) {
      let response = null;
      let params = {
        mutation: WALL_EDIT,
        variables: data_,
      };
      await apolloClient
        .mutate(params)
        .then((res) => {
          if (!res.loading) response = res.data.editarMuroAdmin;
          if (response != null) {
            commit("EDIT_POST", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async addProfile({ commit }, data_) {
      let response = null;
      let params = {
        mutation: WALL_ADD_PROFILE,
        variables: data_,
      };
      await apolloClient
        .mutate(params)
        .then((res) => {
          if (!res.loading) response = res.data.CreatePerfil;
          if (response != null) {
            commit("ADD_PROFILE", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async editProfile({ commit }, data_) {
      let response = null;
      let params = {
        mutation: WALL_EDIT_PROFILE,
        variables: data_,
      };
      await apolloClient
        .mutate(params)
        .then((res) => {
          if (!res.loading) response = res.data.UpdatePerfilMuro;
          if (response != null) {
            commit("UPDATE_PROFILE", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    async removeProfile({ commit }, data_) {
      let response = null;
      let params = {
        mutation: WALL_DELETE_PROFILE,
        variables: data_,
      };
      await apolloClient
        .mutate(params)
        .then((res) => {
          if (!res.loading) response = res.data.DeletePerfilMuro.id_perfil;
          if (response != null) {
            commit("REMOVE_PROFILE", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    // Una noticia
    async getOnePostById({ commit }, data_) {
      let response = null;
      let params = {
        query: GET_POSTS_BY_PAGE,
        variables: data_,
        fetchPolicy: "network-only",
      };
      await apolloClient
        .query(params)
        .then((res) => {
          if (!res.loading) response = res.data.PublicacionesMuroPaginas;
          if (response != null) {
            commit("SET_POST_FOR_EDIT", response);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return new Promise((resolve) => {
        resolve(response);
      });
    },
    // El catálogo depende de si la empresa modera, así que se pide al backend en
    // vez de armarlo aquí. id_estado llega como String aunque el filtro de
    // TotalPaginasPublicacionesMuro reciba Int: se convierte al consultar.
    async getWallStates({ commit }) {
      let response = null;
      try {
        const res = await apolloClient.query({
          query: GET_WALL_STATES,
          fetchPolicy: "network-only",
        });
        if (!res.loading && res.data && res.data.FindEstadosMuro) {
          response = res.data.FindEstadosMuro;
          commit("SET_WALL_STATES", response);
        }
      } catch (err) {
        console.error("Error al obtener estados del muro:", err);
      }
      return response;
    },
    async getWallPermissions({ commit }) {
      let response = null;
      let params = {
        query: GET_WALL_PERMISSIONS,
        fetchPolicy: "network-only",
      };
      try {
        const res = await apolloClient.query(params);
        if (!res.loading && res.data && res.data.GetPermisos) {
          response = res.data.GetPermisos;
          commit("SET_WALL_PERMISSIONS_CATALOG", response);
        }
      } catch (err) {
        console.error("Error al obtener catálogo de permisos de muro:", err);
      }
      return response;
    },
    async getCompanyWallPermissions(
      { commit, dispatch, rootGetters },
      idEmpresaParam,
    ) {
      let response = null;
      const id_empresa =
        idEmpresaParam || (rootGetters && rootGetters["loginModule/idEmpresa"]);
      commit("SET_WALL_SETTINGS_LOADING", true);
      try {
        await dispatch("getWallPermissions");
        if (id_empresa) {
          let params = {
            query: GET_WALL_COMPANY_PERMISSIONS,
            variables: { id_empresa: String(id_empresa) },
            fetchPolicy: "network-only",
          };
          const res = await apolloClient.query(params);
          if (!res.loading && res.data && res.data.GetPermisoEmpresa) {
            response = res.data.GetPermisoEmpresa;
            commit("SET_WALL_COMPANY_PERMISSIONS", response);

            if (Array.isArray(response) && response.length > 0) {
              const settings = {
                moderation: false,
                user_posts: true,
                comments: true,
              };

              response.forEach((item) => {
                const perm = item.permiso;
                if (!perm) return;

                let matched = false;
                Object.keys(SETTING_BY_FLAG).forEach((flag) => {
                  if (perm[flag] !== null && perm[flag] !== undefined) {
                    settings[SETTING_BY_FLAG[flag]] = Boolean(perm[flag]);
                    matched = true;
                  }
                });

                // Catálogo legado: filas "Con moderación" (1) y "Sin
                // moderación" (2) creadas por el seeder sin flags. id_permiso
                // llega como String desde GraphQL.
                if (!matched) {
                  if (String(perm.id_permiso) === "1") {
                    settings.moderation = true;
                  } else if (String(perm.id_permiso) === "2") {
                    settings.moderation = false;
                  }
                }
              });

              commit("SET_WALL_SETTINGS", settings);
            }
          }
        }
      } catch (err) {
        console.error("Error al obtener permisos de muro de empresa:", err);
      } finally {
        commit("SET_WALL_SETTINGS_LOADING", false);
      }
      return response;
    },
    // Lanza el error hacia el componente para que muestre el motivo real. No
    // toca `wall_settings` salvo que el backend confirme el guardado.
    async saveWallSettings({ commit, state, dispatch }, { settings }) {
      commit("SET_WALL_SETTINGS_LOADING", true);
      try {
        if (
          !state.wall_permissions_catalog ||
          state.wall_permissions_catalog.length === 0
        ) {
          await dispatch("getWallPermissions");
        }

        const { ids, missing } = buildPermissionIds(
          state.wall_permissions_catalog,
          settings,
        );

        if (ids.length === 0) {
          throw new Error(
            "No se pudo determinar ningún permiso para guardar: el catálogo de permisos del muro está vacío.",
          );
        }
        if (missing.length > 0) {
          throw new Error(
            `El catálogo no tiene una opción para: ${missing.join(", ")}. ` +
              "Guardar dejaría esa configuración sin registro.",
          );
        }

        const res = await apolloClient.mutate({
          mutation: WALL_SAVE_PERMISSIONS,
          variables: { id_permiso: ids },
        });
        const response =
          res && res.data ? res.data.createOrUpdatePermisos : null;

        if (!Array.isArray(response) || response.length === 0) {
          throw new Error(
            "El servidor no devolvió los permisos guardados. La configuración no se aplicó.",
          );
        }

        commit("SET_WALL_COMPANY_PERMISSIONS", response);
        commit("SET_WALL_SETTINGS", settings);
        return { success: true, response };
      } finally {
        commit("SET_WALL_SETTINGS_LOADING", false);
      }
    },
    // --- Palabras prohibidas -------------------------------------------------
    // El backend guarda las palabras en minúsculas y deduplica por nombre, así
    // que `CreateBannedWords` devuelve la fila existente si la palabra ya está.
    async getBannedWords({ commit }) {
      let response = null;
      try {
        const res = await apolloClient.query({
          query: GET_WALL_BANNED_WORDS,
          fetchPolicy: "network-only",
        });
        if (!res.loading && res.data && res.data.CheckBannedWordsAdmin) {
          response = res.data.CheckBannedWordsAdmin;
          commit("SET_BANNED_WORDS", response);
        }
      } catch (err) {
        console.error("Error al obtener palabras prohibidas:", err);
      }
      return response;
    },
    async addBannedWords({ commit }, palabras) {
      const lista = Array.isArray(palabras) ? palabras : [palabras];
      const res = await apolloClient.mutate({
        mutation: WALL_ADD_BANNED_WORDS,
        variables: { palabras: lista },
      });
      const response = res && res.data ? res.data.CreateBannedWords : null;
      if (!Array.isArray(response) || response.length === 0) {
        throw new Error("El servidor no devolvió la palabra agregada.");
      }
      commit("ADD_BANNED_WORDS", response);
      return response;
    },
    async deleteBannedWord({ commit }, id_palabra) {
      const res = await apolloClient.mutate({
        mutation: WALL_DELETE_BANNED_WORD,
        variables: { id_palabra: parseInt(id_palabra, 10) },
      });
      const response = res && res.data ? res.data.DeleteBannedWords : null;
      if (!response) {
        throw new Error("El servidor no confirmó la eliminación.");
      }
      commit("REMOVE_BANNED_WORD", response.id_palabra);
      return response;
    },
    // No existe mutación de edición en el backend: se crea la nueva y se elimina
    // la anterior, en ese orden, para no perder la palabra si el alta falla.
    async editBannedWord({ dispatch }, { word, nueva_palabra }) {
      const anterior = (word.palabra || "").trim().toLowerCase();
      const nueva = (nueva_palabra || "").trim().toLowerCase();

      // Mismo texto ignorando mayúsculas: crear devolvería la misma fila y el
      // borrado posterior eliminaría la palabra.
      if (!nueva || nueva === anterior) {
        return null;
      }

      const creadas = await dispatch("addBannedWords", [nueva]);
      await dispatch("deleteBannedWord", word.id_palabra);
      return creadas[0];
    },
  },
};
